import type { JobEntry } from '../jobsSlice'
import { isTerminalStatus, type StudioJob, type TranslatedCounts } from '../studioTypes'
import { readProgress } from './jobProgress'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export interface RunCounts {
  lessons: number
  blocks: number
  images: number
}

export type FinishedStatus = 'success' | 'failed' | 'cancelled'

export type TranslationRunView =
  | { kind: 'none' }
  | { kind: 'unknown' }
  | { kind: 'running'; jobId: number; percent: number; stage: 'text' | 'images' }
  | {
      kind: 'finished'
      jobId: number
      status: FinishedStatus
      counts: RunCounts
      job: StudioJob
      /** Закончилась картиночная половина цепочки — текст до неё дошёл целиком
       *  (картинки ставятся только после успеха перевода). */
      textDone: boolean
    }

const NONE: TranslationRunView = { kind: 'none' }
const UNKNOWN: TranslationRunView = { kind: 'unknown' }

const isTranslationRun = (entry: JobEntry): boolean =>
  entry.type === 'course_translation' || Boolean(entry.translationChain)

/** Снимок перевода у картиночной половины цепочки. У продолжения (`resume`)
 *  снимок — картиночный родитель, и перевода в нём нет. */
const chainSnapshot = (entry: JobEntry) =>
  entry.translationChain && entry.previous?.kind === 'chain' ? entry.previous : null

const ratio = (done: number, total: number): number =>
  total > 0 ? Math.min(100, Math.round((done * 100) / total)) : 0

/** Ожидаемое число картинок со старта (смета) — только пока сервер не назвал
 *  своё `images_total`. У записей из persist до этой версии его нет — ноль. */
const expectedImages = (entry: JobEntry): number => entry.imagesTotal ?? 0

/** Числа перевода из снимка цепочки; без снимка — нули. */
const chainPart = (entry: JobEntry) => {
  const chain = chainSnapshot(entry)?.progress
  return { done: chain?.done ?? 0, total: chain ? (chain.total ?? chain.done) : 0 }
}

/**
 * Общая полоса на текст и картинки (§6.4): знаменатель при перецеплении на
 * картиночную джобу РАСШИРЯЕТСЯ числами перевода, а не сбрасывается.
 *
 * Пока сервер не назвал `images_total` (текст ещё идёт, или картиночная джоба
 * не успела отчитаться), в знаменателе стоит ожидаемое число картинок из
 * сметы: иначе полоса доходила бы до 100% на конце текста и откатывалась
 * назад, как только картинки объявят свой план. Настоящее `images_total`,
 * когда оно есть, вытесняет оценку — формула контракта остаётся точной.
 */
const percentOf = (entry: JobEntry, job: StudioJob): number => {
  const own = readProgress(job, entry.total)
  const chain = chainPart(entry)
  const isText = job.type === 'course_translation'
  const ownTotal = own.total ?? (isText ? 0 : expectedImages(entry))
  const pendingImages = isText ? expectedImages(entry) : 0
  return ratio(own.done + chain.done, ownTotal + chain.total + pendingImages)
}

/** Стык цепочки: реестр уже указывает на картиночную джобу, а опрос ещё
 *  держит ответ перевода. Картинки ещё не отчитались — полоса стоит на
 *  числах снимка, а не проваливается в «проверяем…». */
const handoffPercent = (entry: JobEntry): number => {
  const chain = chainPart(entry)
  return ratio(chain.done, chain.total + expectedImages(entry))
}

/** Перевод закончен, а бэк уже поставил картинки: цепочка перецепится на
 *  следующем тике (`useTranslationChain`) — показывать «готово» рано. */
const awaitsChain = (job: StudioJob): boolean =>
  job.type === 'course_translation' && job.status === 'success' && Boolean(job.result?.next_job_id)

const translatedOf = (entry: JobEntry, job: StudioJob): TranslatedCounts | undefined =>
  job.type === 'course_translation' ? job.result?.translated : chainSnapshot(entry)?.translated

/** Картинки — по непустым ссылкам: пустая строка значит «место исчезло»
 *  (`runOutcomeView`), а копия без текста (`images_reused`) — готовое место. */
const imagesOf = (job: StudioJob): number =>
  job.type === 'course_image_generation'
    ? Object.values(job.result?.images ?? {}).filter(Boolean).length
    : 0

const countsOf = (entry: JobEntry, job: StudioJob): RunCounts => {
  const translated = translatedOf(entry, job)
  return { lessons: translated?.lesson ?? 0, blocks: translated?.block ?? 0, images: imagesOf(job) }
}

/**
 * Вид прогона ПЕРЕВОДА для меню языка — единственное место, где его считают.
 * Чистая функция над записью реестра и ответом опроса: `JobPoller` уже ведёт
 * опрос, своего тут нет.
 */
export const translationRunView = (
  entry: JobEntry | null,
  job: StudioJob | null,
): TranslationRunView => {
  if (!entry || !isTranslationRun(entry)) return NONE
  // Запись могла пролежать в persist: без ответа числа — ложь.
  if (!job) return UNKNOWN
  if (job.id !== entry.jobId) {
    // Стык «текст → картинки»: `useCourseJob` ещё отдаёт джобу перевода, а
    // запись уже перецеплена. «Неизвестно» здесь сорвало бы полосу и съело
    // переход «идёт → закончен», на котором всплывает тост итога.
    return chainSnapshot(entry)
      ? { kind: 'running', jobId: entry.jobId, percent: handoffPercent(entry), stage: 'images' }
      : UNKNOWN
  }
  if (!isTerminalStatus(job.status) || awaitsChain(job)) {
    return {
      kind: 'running',
      jobId: job.id,
      percent: percentOf(entry, job),
      stage: job.type === 'course_image_generation' ? 'images' : 'text',
    }
  }
  return {
    kind: 'finished',
    jobId: job.id,
    status: job.status as FinishedStatus,
    counts: countsOf(entry, job),
    job,
    textDone: job.type === 'course_image_generation' && chainSnapshot(entry) !== null,
  }
}

type Messages = (typeof messages)['ru']

const HEAD: Record<FinishedStatus, (m: Messages) => string> = {
  success: (m) => m.runSuccess,
  cancelled: (m) => m.runCancelled,
  failed: (m) => m.runFailed,
}

/** Итог словами, без счётчиков (решение заказчика). `textDone` — признак вида: текст цепочки
 *  переведён целиком, а упали картинки; «перевод прервался» тут соврал бы. */
export const runSummaryText = (status: FinishedStatus, textDone = false): string => {
  const m = messagesNow(messages)
  if (status === 'failed' && textDone) return m.runImagesFailed
  return HEAD[status](m)
}
